import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { AnalyticsEvent, PartnerBank, PartnerInstitutionKind, Routes } from "@ctypes";
import { usePartnerBank } from "@hooks";
import { i18n, Translations } from "@i18n";
import { Analytics } from "@services";
import { PartnershipInfoCard } from "./PartnershipInfoCard";
import {
  Badge,
  Disclaimer,
  Dismiss,
  Page,
  PrimaryCta,
  Separator,
  Subtitle,
  Title,
  TitleHighlight,
  Watermark,
} from "./styles";

const PartnershipContent = ({ partnerBank }: { partnerBank: PartnerBank }) => {
  const navigate = useNavigate();
  const { colors, logos, shortName, accountName, institutionKind, watermarkStyle } = partnerBank;
  const { Light: PartnerLogo, Watermark: PartnerWatermark } = logos;

  // Credit unions are NCUA insured and take deposits as shares, so they cannot
  // use the FDIC sentence.
  const disclaimerKey =
    institutionKind === PartnerInstitutionKind.CreditUnion
      ? Translations.PartnershipPageDisclaimerCreditUnion
      : Translations.PartnershipPageDisclaimer;

  const handleStartInvesting = () => {
    Analytics.track(AnalyticsEvent.PartnershipPrimaryCTAPressed, { partner: partnerBank.displayName });
    navigate(Routes.Dashboard);
  };

  const handleDismiss = () => {
    Analytics.track(AnalyticsEvent.PartnershipSecondaryCTAPressed, {
      partner: partnerBank.displayName,
    });
    navigate(Routes.Dashboard);
  };

  return (
    <Page $colors={colors}>
      {PartnerWatermark && (
        <Watermark aria-hidden="true" style={watermarkStyle}>
          <PartnerWatermark />
        </Watermark>
      )}

      <Badge>
        <PartnerLogo height={logos.badgeHeight} />
        <Separator>+</Separator>
        <span>FutureMoney</span>
      </Badge>

      <Title>
        {i18n.t(Translations.PartnershipPageTitle1, { bank: shortName })}
        <TitleHighlight $color={colors.titleHighlight}>
          {i18n.t(Translations.PartnershipPageTitleHighlight)}
        </TitleHighlight>
        {i18n.t(Translations.PartnershipPageTitle2)}
      </Title>

      <Subtitle>{i18n.t(Translations.PartnershipPageSubtitle, { bank: accountName })}</Subtitle>

      <PartnershipInfoCard colors={colors} />

      <PrimaryCta type="button" onClick={handleStartInvesting} $colors={colors}>
        {i18n.t(Translations.PartnershipPageCTA)}
      </PrimaryCta>

      <Dismiss type="button" onClick={handleDismiss}>
        {i18n.t(Translations.PartnershipPageDismiss)}
      </Dismiss>

      <Disclaimer>{i18n.t(disclaimerKey, { bank: accountName })}</Disclaimer>
    </Page>
  );
};

export const PartnershipRoute = () => {
  const navigate = useNavigate();
  const { partnerBank } = usePartnerBank();

  useEffect(() => {
    Analytics.page(Routes.Partnership);
  }, []);

  // The partner resolves to null if its flag is switched off mid-session, or if
  // the page is opened directly by someone with no partner, so send them home
  // rather than render an unthemed page.
  useEffect(() => {
    if (!partnerBank) navigate(Routes.Dashboard, { replace: true });
  }, [partnerBank, navigate]);

  if (!partnerBank) return null;

  return <PartnershipContent partnerBank={partnerBank} />;
};
